import React, { useState, useEffect, useContext, useMemo } from "react";
import { m as motion, AnimatePresence } from "framer-motion";
import toast from "react-hot-toast";
import * as Dialog from "@radix-ui/react-dialog";
import { v4 as uuidv4 } from "uuid";
import UploadModal from "./UploadModal";
import { useUserAuth } from "../context/UserAuthContext";
import { MyContext } from "./Structure";
import { getCurrentTimeInUTCFormat, ParseDate } from "../../common/methods";
import { useRef } from "react";
import { FaRegShareFromSquare, FaCheck, FaTrashCan, FaMagnifyingGlass, FaExpand } from "react-icons/fa6";
import { IoCopy, IoCopyOutline, IoSquareOutline, IoCheckbox, IoFilterOutline, IoCloudUploadOutline, IoDownloadOutline, IoChevronBack, IoChevronForward, IoClose } from "react-icons/io5";
import { MdDeleteOutline, MdDeleteSweep, MdClear, MdCloudDone } from "react-icons/md";
import { PiWarningFill, PiImageSquareDuotone, PiVideoCameraDuotone, PiFileTextDuotone, PiSpinnerBold } from "react-icons/pi";
import { uploadFileToCloudinary } from "../../config/cloudinary";
import { db } from "../../config/firebase";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import {
  collection,
  addDoc,
  getDocs,
  query,
  where,
  deleteDoc,
  doc,
  orderBy
} from "firebase/firestore";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";

const ImageUploader = () => {
  const queryClient = useQueryClient();
  const location = useLocation();
  const { calculateTotalFileSize, totalSize, totalSizePercent } =
    useContext(MyContext);
  const imagepoped = useRef(null);
  const galleryMediaRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const { user } = useUserAuth();
  const [copySuccess, setCopySuccess] = useState("");
  const [deleteId, setDeleteId] = useState(null);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [previewFile, setPreviewFile] = useState(null);
  const [viewMode, setViewMode] = useState("all"); // 'all' or 'cloudinary'

  const [filterType, setFilterType] = useState("all"); // 'all', 'image', 'video', 'application'
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadQueue, setUploadQueue] = useState([]); // {id, name, status, progress, size}
  const [isBulkDownloading, setIsBulkDownloading] = useState(false);
  const cancelledUploads = useRef(new Set());

  // TanStack Query for fetching files
  const { data: imgUrl = [], isLoading: loading, refetch } = useQuery({
    queryKey: ["files", user?.email],
    queryFn: async () => {
      if (!user || !user.email) return [];
      const q = query(
        collection(db, "files"),
        where("userEmail", "==", user.email),
        orderBy("timeCreated", "desc")
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        ...doc.data(),
        id: doc.id,
        showDropdown: false
      }));
    },
    enabled: !!user?.email,
  });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const q = params.get("q");
    if (q !== null) {
      setSearchQuery(q);
    }
    
    const previewId = params.get("previewId");
    if (previewId && imgUrl.length > 0) {
      const fileToPreview = imgUrl.find(file => file.id === previewId);
      if (fileToPreview) {
        setPreviewFile(fileToPreview);
      }
    }
  }, [location.search, imgUrl]);

  const filteredImgUrl = useMemo(() => {
    return imgUrl.filter((file) => {
      const matchesSearch = file.name.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesFilter = filterType === "all" || file.contentType.startsWith(filterType);
      const matchesView = viewMode === "all" || Boolean(file.publicId);
      return matchesSearch && matchesFilter && matchesView;
    });
  }, [imgUrl, searchQuery, filterType, viewMode]);

  useEffect(() => {
    if (user && user.email) {
      calculateTotalFileSize();
    }
    const handleBeforeUnload = (event) => {
      if (uploading) {
        event.preventDefault();
        event.returnValue = "";
        const result = window.confirm("Are you sure you want to proceed?");
        if (result) {
          window.location.reload();
        }
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [user.email]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!previewFile) return;
      
      const currentIndex = filteredImgUrl.findIndex(f => f.id === previewFile.id);
      if (currentIndex === -1) return;

      if (e.key === "ArrowLeft") {
        const nextIndex = (currentIndex - 1 + filteredImgUrl.length) % filteredImgUrl.length;
        setPreviewFile(filteredImgUrl[nextIndex]);
      } else if (e.key === "ArrowRight") {
        const nextIndex = (currentIndex + 1) % filteredImgUrl.length;
        setPreviewFile(filteredImgUrl[nextIndex]);
      } else if (e.key === "Escape") {
        setPreviewFile(null);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [previewFile, filteredImgUrl]);

  const onDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const onDragLeave = () => {
    setIsDragging(false);
  };

  const onDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleUpload(files);
    }
  };

  const handleUpload = async (uploadedImgs) => {
    try {
      setUploading(true);
      const maxSize = 100 * 1024 * 1024; // 100 MB
      const currentTotalSize = imgUrl.reduce((acc, file) => acc + file.size, 0);
      const existingFileNames = new Set(imgUrl.map((file) => file.name));

      // Batch initial queue entry
      const newItems = Array.from(uploadedImgs).map(file => ({
        id: uuidv4(),
        name: file.name,
        status: "waiting",
        size: file.size,
        progress: 0
      }));
      setUploadQueue(prev => [...prev, ...newItems]);

      for (let i = 0; i < uploadedImgs.length; i++) {
        const queueId = newItems[i].id;
        
        // Skip if this specific file was cancelled before processing started
        if (cancelledUploads.current.has(queueId)) {
          cancelledUploads.current.delete(queueId);
          continue;
        }

        const uploadedImg = uploadedImgs[i];
        const filename = uploadedImg.name;
        const nameWithoutExtension = filename.split(".").slice(0, -1).join(".");
        let name = nameWithoutExtension;
        let counter = 1;
        while (existingFileNames.has(name)) {
          name = `${nameWithoutExtension} (copy ${counter})`;
          counter++;
        }

        const fileType = uploadedImg.type;
        // Accept common types (images, videos, applications/docs)
        if (fileType.startsWith("image/") || fileType.startsWith("video/") || fileType === "application/pdf" || fileType.includes("msword") || fileType.includes("officedocument")) {
          
          if (currentTotalSize + uploadedImg.size <= maxSize) {
            setUploadQueue(prev => prev.map(item => 
              item.id === queueId ? { ...item, status: "uploading" } : item
            ));

            try {
              const uploadPromise = uploadFileToCloudinary(uploadedImg);
              const result = await uploadPromise;
              
              // Final check before adding to DB
              if (cancelledUploads.current.has(queueId)) {
                cancelledUploads.current.delete(queueId);
                continue; 
              }

              setUploadQueue(prev => prev.map(item => 
                item.id === queueId ? { ...item, status: "saving", progress: 100 } : item
              ));

              const url = result.secure_url;
              const fileData = {
                url,
                name,
                userEmail: user.email,
                timeCreated: getCurrentTimeInUTCFormat(),
                size: uploadedImg.size,
                contentType: uploadedImg.type,
              };

              await addDoc(collection(db, "files"), fileData);
              existingFileNames.add(name);

              setUploadQueue(prev => prev.map(item => 
                item.id === queueId ? { ...item, status: "completed" } : item
              ));

              // Auto-remove from queue after success
              setTimeout(() => {
                setUploadQueue(prev => prev.filter(item => item.id !== queueId));
              }, 3000);

            } catch (err) {
              setUploadQueue(prev => prev.map(item => 
                item.id === queueId ? { ...item, status: "error" } : item
              ));
              toast.error(`Upload failed: ${uploadedImg.name}`);
            }
          } else {
            setUploadQueue(prev => prev.map(item => 
              item.id === queueId ? { ...item, status: "error", error: "Size Limit" } : item
            ));
            toast.error("Cloud Storage Full (100MB Limit)");
          }
        } else {
          setUploadQueue(prev => prev.map(item => 
            item.id === queueId ? { ...item, status: "error", error: "Invalid Type" } : item
          ));
          toast.error("Unsupported file type");
        }
      }

      queryClient.invalidateQueries(["files", user?.email]);
      await calculateTotalFileSize();
      setUploading(false);
      return true;
    } catch (error) {
      setUploading(false);
      console.error("Error uploading files:", error);
      return false;
    }
  };

  const handleDelete = async (id, url) => {
    try {
      await deleteDoc(doc(db, "files", id));
      queryClient.invalidateQueries(["files", user?.email]);
      setDeleteId(null);
      setSelectedFiles((prev) => prev.filter((fileId) => fileId !== id));
      toast.success("File Deleted Successfully", { position: "top-center" });
      await calculateTotalFileSize();
    } catch (error) {
      console.error("Error deleting document:", error);
      toast.error("Failed to delete file from record", { position: "top-center" });
    }
  };

  const handleBulkDelete = async () => {
    try {
      setIsBulkDeleting(true);
      const deletePromises = selectedFiles.map((id) => deleteDoc(doc(db, "files", id)));

      toast.promise(
        Promise.all(deletePromises),
        {
          loading: `Deleting ${selectedFiles.length} files...`,
          success: `${selectedFiles.length} files deleted successfully!`,
          error: "Failed to delete some files",
        },
        { position: "top-center" }
      );

      await Promise.all(deletePromises);
      queryClient.invalidateQueries(["files", user?.email]);
      setSelectedFiles([]);
      await calculateTotalFileSize();
    } catch (error) {
      console.error("Error in bulk delete:", error);
    } finally {
      setIsBulkDeleting(false);
    }
  };

  const toggleSelectFile = (id) => {
    setSelectedFiles((prev) =>
      prev.includes(id) ? prev.filter((fileId) => fileId !== id) : [...prev, id]
    );
  };

  const selectAllFiles = () => {
    if (selectedFiles.length === imgUrl.length) {
      setSelectedFiles([]);
    } else {
      setSelectedFiles(imgUrl.map((file) => file.id));
    }
  };

  const handleCopy = (dataVal) => {
    navigator.clipboard.writeText(dataVal.url);
    setCopySuccess(dataVal.url);
    setTimeout(() => {
      setCopySuccess("");
      toggleDropdown(dataVal.id);
    }, 1000);
  };

  const share = (dataVal) => {
    if (navigator.share) {
      navigator
        .share({
          title: dataVal.name,
          url: dataVal.url.replaceAll(" ", "%20"),
        })
        .then(() => {
          console.log("ok");
        })
        .catch((err) => {
          console.error(err);
          alert(err);
        });
    } else {
      console.log("Doesnt support");
    }
  };

  const toggleDropdown = (id) => {
    // Note: We use queryClient.setQueryData to update local state for UI responsiveness
    // specifically for the dropdown menu visibility which doesn't need a refetch
    queryClient.setQueryData(["files", user?.email], (oldData) =>
      oldData?.map((image) => {
        if (image.id === id) {
          return { ...image, showDropdown: !image.showDropdown };
        }
        return { ...image, showDropdown: false };
      })
    );
  };

  const bytesToMB = (bytes) => {
    if (bytes === 0) return "0 MB";
    if (bytes < 1024) return `${bytes} bytes`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    const megabytes = bytes / (1024 * 1024);
    return `${megabytes.toFixed(2)} MB`;
  };

  const cancelUpload = (id) => {
    cancelledUploads.current.add(id);
    setUploadQueue(prev => prev.filter(item => item.id !== id));
    toast.error("Upload cancelled", { duration: 2000 });
  };

  const handleDownload = async (file) => {
    try {
      const response = await fetch(file.url);
      const blob = await response.blob();
      const extension = file.contentType.split("/")[1]?.split("+")[0] || "bin";
      const fileName = file.name.includes(".") ? file.name : `${file.name}.${extension}`;
      saveAs(blob, fileName);
      toast.success("Download started");
    } catch (error) {
      console.error("Download failed:", error);
      toast.error("Download failed. Please try again.");
    }
  };

  const handleBulkDownload = async () => {
    if (selectedFiles.length === 0) return;
    
    setIsBulkDownloading(true);
    const zip = new JSZip();
    const filesToDownload = imgUrl.filter(file => selectedFiles.includes(file.id));
    
    const downloadPromise = (async () => {
      for (const file of filesToDownload) {
        try {
          const response = await fetch(file.url);
          const blob = await response.blob();
          const extension = file.contentType.split("/")[1]?.split("+")[0] || "bin";
          const fileName = file.name.includes(".") ? file.name : `${file.name}.${extension}`;
          zip.file(fileName, blob);
        } catch (err) {
          console.error(`Failed to fetch ${file.name}:`, err);
        }
      }
      const content = await zip.generateAsync({ type: "blob" });
      saveAs(content, `cloud-point-files-${getCurrentTimeInUTCFormat()}.zip`);
    })();

    toast.promise(
      downloadPromise,
      {
        loading: `Zipping ${selectedFiles.length} files...`,
        success: "Bulk download started!",
        error: "Failed to create zip file",
      },
      { position: "top-center" }
    );

    try {
      await downloadPromise;
    } finally {
      setIsBulkDownloading(false);
    }
  };

  return (
    <div 
      className={`md:container mx-auto px-8 w-full bg-white h-[90vh] overflow-y-scroll max-h-[90vh] transition-all duration-200 relative ${isDragging ? "bg-blue-50/50 outline-2 outline-dashed outline-[#049be7] outline-offset-[-10px] z-50" : ""}`}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      {/* Drop overlay message */}
      <AnimatePresence>
        {isDragging && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-[#049be7]/10 backdrop-blur-[2px] z-[100] flex items-center justify-center pointer-events-none"
          >
            <motion.div 
              initial={{ scale: 0.8, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.8, y: 20 }}
              className="bg-white p-8 rounded-3xl shadow-2xl flex flex-col items-center gap-4 border-2 border-[#049be7]"
            >
              <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
              >
                <IoCloudUploadOutline className="text-6xl text-[#049be7]" />
              </motion.div>
              <p className="text-2xl font-bold text-gray-800">Drop files to upload</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating Upload Progress */}
      <AnimatePresence>
        {uploadQueue.length > 0 && (
          <motion.div 
            initial={{ opacity: 0, y: 50, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className="fixed bottom-5 right-24 z-[1000] w-80 bg-white rounded-lg shadow-lg border border-gray-100 overflow-hidden"
          >
          <div className="bg-gray-800 px-4 py-3 flex justify-between items-center text-white">
            <h3 className="text-sm font-bold flex items-center gap-2">
              {uploadQueue.every(f => f.status === "completed") ? (
                <MdCloudDone className="text-green-400 text-lg" />
              ) : (
                <PiSpinnerBold className="animate-spin text-[#049be7]" />
              )}
              {uploadQueue.every(f => f.status === "completed") ? "Uploads Complete" : `Queue (${uploadQueue.filter(f => f.status !== "completed").length})`}
            </h3>
            <button 
              onClick={() => {
                uploadQueue.forEach(f => {
                   if (f.status !== "completed") cancelledUploads.current.add(f.id);
                });
                setUploadQueue([]);
              }}
              title="Cancel all pending uploads"
              className="text-gray-400 hover:text-white transition-colors"
            >
              <MdClear />
            </button>
          </div>
          <div className="max-h-60 overflow-y-auto p-2 space-y-2">
            {uploadQueue.map((file) => (
              <div key={file.id} className="p-3 bg-gray-50 rounded-xl border border-gray-100 group">
                <div className="flex justify-between items-start mb-2">
                  <div className="flex-1 min-w-0 pr-2">
                    <p className="text-xs font-bold text-gray-700 truncate">{file.name}</p>
                    <p className="text-[10px] text-gray-400">{bytesToMB(file.size)}</p>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {file.status === "completed" && <FaCheck className="text-green-500 text-xs mt-1" />}
                    {file.status === "error" && <PiWarningFill className="text-red-500 text-xs mt-1" />}
                    {(file.status === "waiting" || file.status === "uploading") && (
                      <button 
                        onClick={() => cancelUpload(file.id)}
                        className="text-gray-300 hover:text-red-500 transition-colors p-1"
                        title="Cancel upload"
                      >
                        <MdClear className="text-sm" />
                       </button>
                    )}
                  </div>
                </div>
                
                {file.status !== "completed" && file.status !== "error" && (
                  <div className="w-full bg-gray-200 rounded-full h-1.5 overflow-hidden">
                    <div 
                      className={`h-full transition-all duration-300 ${file.status === "saving" ? "bg-green-500 animate-pulse" : "bg-[#049be7]"}`}
                      style={{ width: `${file.status === "saving" ? 100 : 45}%` }} // Cloudinary fetch doesn't give precise progress easily via standard fetch, so we simulate
                    ></div>
                  </div>
                )}
                
                <p className="text-[10px] mt-1 font-semibold uppercase tracking-wider">
                  {file.status === "waiting" && <span className="text-gray-400 text-[9px]">Waiting...</span>}
                  {file.status === "uploading" && <span className="text-blue-500 text-[9px]">Uploading to Cloud...</span>}
                  {file.status === "saving" && <span className="text-amber-500 text-[9px]">Indexing in Database...</span>}
                  {file.status === "completed" && <span className="text-green-500 text-[9px]">Finished!</span>}
                  {file.status === "error" && <span className="text-red-500 text-[9px]">{file.error || "Failed"}</span>}
                </p>
              </div>
            ))}
          </div>
        </motion.div>
      )}
    </AnimatePresence>

      {!loading ? (
        <span>
          {/* First Row: Cloud Usage (Left) | Bulk Actions & Upload (Right) */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mt-8 mb-4 gap-4">
            <span className="flex items-start flex-col justify-center gap-2">
              <span className="block text-sm font-medium text-gray-600">Total Size: {bytesToMB(totalSize)}</span>
              <span className="flex items-center justify-center">
                <div className="lg:w-[400px] w-full bg-gray-200 flex rounded-full me-3 h-4">
                  <div
                    className="bg-[#049be7] h-4 rounded-full"
                    style={{ width: `${totalSizePercent}%` }}
                  ></div>
                </div>
                <span className="text-sm font-semibold">{`${totalSizePercent.toFixed(1)}%`}</span>
              </span>
            </span>

            <div className="flex items-center gap-3 w-full md:w-auto justify-end">
              {imgUrl.length > 0 && (
                <div className="flex items-center gap-2">
                  <AnimatePresence>
                    {selectedFiles.length > 0 && (
                      <motion.div 
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 20 }}
                        className="flex items-center gap-2"
                      >
                        <button
                          onClick={handleBulkDownload}
                          disabled={isBulkDownloading}
                          className="bg-blue-50 text-blue-600 hover:bg-blue-100 px-4 py-2 rounded-xl flex items-center gap-2 transition-all font-semibold border border-blue-100 disabled:opacity-50"
                        >
                          <IoDownloadOutline className="text-xl" />
                          Download {selectedFiles.length}
                        </button>
                        <button
                          onClick={() => setShowBulkDeleteConfirm(true)}
                          disabled={isBulkDeleting}
                          className="bg-red-50 text-red-600 hover:bg-red-100 px-4 py-2 rounded-xl flex items-center gap-2 transition-all font-semibold border border-red-100 disabled:opacity-50"
                        >
                          <MdDeleteSweep className="text-xl" />
                          Delete {selectedFiles.length}
                        </button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                  <button
                    onClick={selectAllFiles}
                    className="text-gray-600 hover:bg-gray-100 px-4 py-2 rounded-xl flex items-center gap-2 transition-all font-medium border border-gray-200"
                  >
                    {selectedFiles.length === imgUrl.length && imgUrl.length > 0 ? "Deselect All" : "Select All"}
                  </button>
                </div>
              )}

              <button
                onClick={() => setIsFilterOpen(!isFilterOpen)}
                className={`p-2 rounded-xl border transition-all flex items-center gap-2 font-medium ${isFilterOpen
                    ? "bg-[#049be7] text-white border-[#049be7] shadow-lg shadow-blue-100"
                    : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
                  }`}
                title="Search and Filters"
              >
                <IoFilterOutline className="text-xl" />
                <span className="hidden md:inline">Filters</span>
              </button>

              <UploadModal handleUpload={handleUpload} bytesToMB={bytesToMB} />
            </div>
          </div>

          <Dialog.Root open={showBulkDeleteConfirm} onOpenChange={setShowBulkDeleteConfirm}>
            <Dialog.Portal>
              <Dialog.Overlay className="bg-black/50 data-[state=open]:animate-overlayShow fixed inset-0 z-[1000000]" />
              <Dialog.Content className="z-[1000001] data-[state=open]:animate-contentShow fixed rounded-2xl top-[50%] left-[50%] w-[90vw] max-w-[450px] translate-x-[-50%] translate-y-[-50%] bg-white p-6 shadow-2xl focus:outline-none">
                <div className="flex flex-col items-center text-center">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                    <PiWarningFill className="text-3xl text-red-600" />
                  </div>
                  <Dialog.Title className="text-xl font-bold text-gray-900 mb-2">
                    Bulk Delete
                  </Dialog.Title>
                  <Dialog.Description className="text-gray-500 mb-6 w-full">
                    Are you sure you want to delete <span className="font-bold text-red-600">{selectedFiles.length}</span> selected files?
                    <div className="mt-4 p-3 bg-gray-50 rounded-xl text-left max-h-[150px] overflow-y-auto border border-gray-100">
                      <p className="text-xs font-semibold text-gray-400 uppercase mb-2">Files to be removed:</p>
                      <ul className="text-sm space-y-1">
                        {imgUrl
                          .filter((file) => selectedFiles.includes(file.id))
                          .slice(0, 10)
                          .map((file) => (
                            <li key={file.id} className="truncate text-gray-700 font-medium text-[13px]">
                              • {file.name}
                            </li>
                          ))}
                        {selectedFiles.length > 10 && (
                          <li className="text-blue-500 font-semibold text-xs mt-2 pl-2">
                            + {selectedFiles.length - 10} more files selected
                          </li>
                        )}
                      </ul>
                    </div>
                    <p className="mt-4 text-xs italic text-red-400">This action cannot be undone.</p>
                  </Dialog.Description>
                  <div className="flex gap-3 w-full">
                    <button
                      onClick={() => setShowBulkDeleteConfirm(false)}
                      className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-xl transition-colors"
                    >
                      Go Back
                    </button>
                    <button
                      onClick={() => {
                        handleBulkDelete();
                        setShowBulkDeleteConfirm(false);
                      }}
                      className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-xl transition-colors shadow-lg shadow-red-200"
                    >
                      Confirm Delete
                    </button>
                  </div>
                </div>
              </Dialog.Content>
            </Dialog.Portal>
          </Dialog.Root>

          {/* Second Row: Search & Filtering (Collapsible) */}
          <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isFilterOpen ? "max-h-40 opacity-100 mb-8" : "max-h-0 opacity-0 mb-0"}`}>
            <div className="flex flex-col md:flex-row justify-between items-center py-4 border-y border-gray-100 bg-gray-50/40 rounded-xl px-4 gap-4">
              <div className="flex flex-wrap items-center gap-4 w-full md:w-auto">
                {/* Search Bar */}
                <div className="relative flex-1 md:flex-initial">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">
                    <FaMagnifyingGlass />
                  </span>
                  <input
                    type="text"
                    placeholder="Search files..."
                    className="w-full md:w-64 pl-10 pr-4 py-2 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#049be7]/20 focus:border-[#049be7] text-sm transition-all"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>

                {/* Filtering Tabs */}
                <div className="flex bg-white p-1 rounded-xl border border-gray-100 items-center shadow-sm">
                  {[
                    { id: "all", icon: <IoFilterOutline />, label: "All" },
                    { id: "image", icon: <PiImageSquareDuotone />, label: "Images" },
                    { id: "video", icon: <PiVideoCameraDuotone />, label: "Videos" },
                    { id: "application", icon: <PiFileTextDuotone />, label: "Docs" }
                  ].map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setFilterType(tab.id)}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${filterType === tab.id
                          ? "bg-[#049be7] text-white shadow-md shadow-blue-100"
                          : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                        }`}
                    >
                      <span className="text-sm">{tab.icon}</span>
                      <span>{tab.label}</span>
                    </button>
                  ))}
                </div>

                {/* Cloudinary View Toggle */}
                <div className="flex bg-white p-1 rounded-xl border border-gray-100 items-center shadow-sm">
                  {[
                    { id: "all", label: "All Files" },
                    { id: "cloudinary", label: "Cloudinary Files" }
                  ].map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setViewMode(tab.id)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${viewMode === tab.id
                          ? "bg-[#10b981] text-white shadow-md shadow-emerald-100"
                          : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                        }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>

              <p className="text-sm text-gray-500 font-medium">
                Showing <span className="text-[#049be7] font-medium">{filteredImgUrl.length}</span> of <span className="font-medium">{imgUrl.length}</span> items
                {viewMode === "cloudinary" && (
                  <span className="block text-xs text-slate-400 mt-1">
                    Cloudinary view shows files with stored `publicId` metadata.
                  </span>
                )}
              </p>
            </div>
          </div>

          {/* Bulk Delete Confirmation Modal */}
          <Dialog.Root open={showBulkDeleteConfirm} onOpenChange={setShowBulkDeleteConfirm}>
            <Dialog.Portal>
              <Dialog.Overlay className="bg-black/50 data-[state=open]:animate-overlayShow fixed inset-0 z-[1000000]" />
              <Dialog.Content className="z-[1000001] data-[state=open]:animate-contentShow fixed rounded-2xl top-[50%] left-[50%] w-[90vw] max-w-[450px] translate-x-[-50%] translate-y-[-50%] bg-white p-6 shadow-2xl focus:outline-none">
                <div className="flex flex-col items-center text-center">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                    <PiWarningFill className="text-3xl text-red-600" />
                  </div>
                  <Dialog.Title className="text-xl font-bold text-gray-900 mb-2">
                    Bulk Delete
                  </Dialog.Title>
                  <Dialog.Description className="text-gray-500 mb-6 w-full">
                    Are you sure you want to delete <span className="font-bold text-red-600">{selectedFiles.length}</span> selected files?
                    <div className="mt-4 p-3 bg-gray-50 rounded-xl text-left max-h-[150px] overflow-y-auto border border-gray-100">
                      <p className="text-xs font-semibold text-gray-400 uppercase mb-2">Files to be removed:</p>
                      <ul className="text-sm space-y-1">
                        {imgUrl
                          .filter((file) => selectedFiles.includes(file.id))
                          .slice(0, 10)
                          .map((file) => (
                            <li key={file.id} className="truncate text-gray-700 font-medium text-[13px]">
                              • {file.name}
                            </li>
                          ))}
                        {selectedFiles.length > 10 && (
                          <li className="text-blue-500 font-semibold text-xs mt-2 pl-2">
                            + {selectedFiles.length - 10} more files selected
                          </li>
                        )}
                      </ul>
                    </div>
                    <p className="mt-4 text-xs italic text-red-400">This action cannot be undone.</p>
                  </Dialog.Description>
                  <div className="flex gap-3 w-full">
                    <button
                      onClick={() => setShowBulkDeleteConfirm(false)}
                      className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-xl transition-colors"
                    >
                      Go Back
                    </button>
                    <button
                      onClick={() => {
                        handleBulkDelete();
                        setShowBulkDeleteConfirm(false);
                      }}
                      className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-xl transition-colors shadow-lg shadow-red-200"
                    >
                      Confirm Delete
                    </button>
                  </div>
                </div>
              </Dialog.Content>
            </Dialog.Portal>
          </Dialog.Root>

          <motion.div
            layout
            className={`flex flex-wrap items-start justify-center ${filteredImgUrl.length > 0 && "lg:grid lg:grid-cols-3"
              }  gap-5 w-full md:px-5 my-8`}
          >
            <AnimatePresence mode="popLayout">
              {filteredImgUrl.length > 0 ? (
                filteredImgUrl.map((dataVal) => (
                  <motion.div
                    layout
                    key={dataVal.id}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9, transition: { duration: 0.2 } }}
                    whileHover={{ y: -4 }}
                    className={`border border-1 rounded-xl border-slate-300 p-1 w-full lg:w-auto overflow-hidden group transition-all ${selectedFiles.includes(dataVal.id) ? "ring-2 ring-[#049be7]" : ""
                      }`}
                  >
                  <div className="flex bg-gray-100 rounded-t-lg items-center justify-between p-2">
                    <h1 className="p-2 uppercase flex gap-3 items-center justify-center">
                      <button
                        onClick={() => toggleSelectFile(dataVal.id)}
                        className={`transition-all ${selectedFiles.includes(dataVal.id)
                            ? "text-[#049be7]"
                            : selectedFiles.length > 0
                              ? "text-gray-400 opacity-100" // Show for all if at least one is selected
                              : "text-gray-400 opacity-0 group-hover:opacity-100" // Normal hover behavior
                          }`}
                      >
                        {selectedFiles.includes(dataVal.id) ? (
                          <IoCheckbox className="text-xl" />
                        ) : (
                          <IoSquareOutline className="text-xl" />
                        )}
                      </button>
                      {dataVal.contentType
                        .replace(/(image|video|application)\//, "")
                        .slice(0, 6)
                        .replace("+", "")
                        .replace(".", "")}
                      <span className="text-xs">
                        {ParseDate(dataVal.timeCreated)}
                      </span>
                    </h1>
                    <div className="relative inline-block text-left">
                      <button
                        className="bg-gray-100 text-gray-800 mr-2"
                        onClick={() => toggleDropdown(dataVal.id)}
                      >
                        <i
                          className={`fa-solid fa-ellipsis active:scale-75 transition-all rotate-90`}
                        ></i>
                      </button>
                      {dataVal.showDropdown && (
                        <ul className="origin-top-center absolute right-3 mt-2 w-40 rounded-md shadow-lg z-40 bg-white ring-1 ring-black ring-opacity-5">
                          {copySuccess === dataVal.url ? (
                            <li
                              className="py-2 px-3"
                              onClick={() => handleCopy(dataVal)}
                            >
                              <button className="w-full text-left inline-flex items-center gap-2">
                                <IoCopy /> Copied!
                              </button>
                            </li>
                          ) : (
                            <li
                              className="py-2 px-3 hover:bg-gray-100 "
                              onClick={() => handleCopy(dataVal)}
                            >
                              <button className="w-full text-left inline-flex items-center gap-2">
                                <IoCopyOutline /> Copy Url
                              </button>
                            </li>
                          )}
                          <li
                            className="py-2 px-3 hover:bg-gray-100 cursor-pointer"
                            onClick={() => handleDownload(dataVal)}
                          >
                            <button className="w-full text-left inline-flex items-center gap-2">
                              <IoDownloadOutline /> Download
                            </button>
                          </li>
                          <li className="py-2 px-3 active:bg-gray-100 active:text-gray-500 hover:bg-gray-100 rounded-b-md">
                            <button
                              onClick={() => share(dataVal)}
                              className="w-full text-left inline-flex items-center gap-2"
                            >
                              <FaRegShareFromSquare /> Share
                            </button>
                          </li>
                          <li className="py-2 px-3 active:bg-red-200 active:text-red-500 hover:bg-gray-100 rounded-b-md">
                            <button
                              onClick={() => {
                                setDeleteId(dataVal.id);
                                toggleDropdown("");
                              }}
                              className="w-full text-left inline-flex items-center gap-2"
                            >
                              <MdDeleteOutline /> Delete
                            </button>
                          </li>
                        </ul>
                      )}
                    </div>
                  </div>

                  {/* Custom Delete Confirmation Modal */}
                  <Dialog.Root open={deleteId === dataVal.id} onOpenChange={(open) => !open && setDeleteId(null)}>
                    <Dialog.Portal>
                      <Dialog.Overlay className="bg-black/50 data-[state=open]:animate-overlayShow fixed inset-0 z-[1000000]" />
                      <Dialog.Content className="z-[1000001] data-[state=open]:animate-contentShow fixed rounded-2xl top-[50%] left-[50%] w-[90vw] max-w-[600px] translate-x-[-50%] translate-y-[-50%] bg-white p-6 shadow-2xl focus:outline-none">
                        <div className="flex flex-col items-center text-center">
                          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                            <PiWarningFill className="text-3xl text-red-500" />
                          </div>
                          <Dialog.Title className="text-xl font-bold text-gray-900 mb-2">
                            Confirm Deletion
                          </Dialog.Title>
                          <Dialog.Description className="text-gray-500 mb-6">
                            Are you sure you want to delete <span className="font-semibold text-gray-800">"{dataVal.name}"</span>?
                            This action cannot be undone and the file will be permanently removed.
                          </Dialog.Description>
                          <div className="flex gap-3 w-full">
                            <button
                              onClick={() => setDeleteId(null)}
                              className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-xl transition-colors"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={() => handleDelete(deleteId)}
                              className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-xl transition-colors shadow-lg shadow-red-200"
                            >
                              Delete File
                            </button>
                          </div>
                        </div>
                      </Dialog.Content>
                    </Dialog.Portal>
                  </Dialog.Root>

                  <div 
                    onClick={() => setPreviewFile(dataVal)}
                    className="flex items-center justify-center cursor-pointer group-hover:scale-[1.02] transition-transform duration-200"
                  >
                    {dataVal.contentType.includes("image") ? (
                      <img
                        src={dataVal.url}
                        alt="uploaded-img"
                        className="w-full bg-gray-50 max-h-[200px] object-contain rounded-t-lg"
                      />
                    ) : dataVal.contentType.includes("video") ? (
                      <video
                        src={dataVal.url}
                        alt="uploaded-img"
                        className="w-full max-h-[200px] bg-black rounded-t-lg"
                      ></video>
                    ) : (
                      <div className="w-full max-h-[200px] min-h-[200px] bg-gray-50 flex items-center justify-center flex-col px-5 rounded-t-lg">
                        <i className="fas fa-file h-32 w-16 text-gray-400"></i>
                        <span className="text-gray-400 font-mono text-xs uppercase mt-2">
                        {dataVal.contentType
                          .replace("application/", "")
                          .replace("-", "")
                          .slice(0, 10)}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="text-center py-2 bg-gray-100 rounded-b-lg border-t border-gray-200 relative">
                    <h1
                      title={dataVal.name}
                      className="text-md beak-all font-semibold"
                    >
                      {dataVal.name.length > 15
                        ? `${dataVal.name.slice(0, 15)}...`
                        : `${dataVal.name}.${dataVal.contentType
                          .replace("image/", "")
                          .replace("video/", "")
                          .slice(0, 4)}`}
                    </h1>
                    <h1 className="text-sm mt-1">{bytesToMB(dataVal.size)}</h1>
                  </div>
                </motion.div>
              ))
            ) : (
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex items-center w-full h-[60vh] py-5 my-5 justify-center flex-col"
              >
                <img
                  src="https://user-images.githubusercontent.com/507615/54591670-ac0a0180-4a65-11e9-846c-e55ffce0fe7b.png"
                  alt="NO API KEY"
                  className="w-44"
                />
                <p className="text-base">Not Yet Uploaded Anything...</p>
              </motion.div>
            )}
            </AnimatePresence>
          </motion.div>
        </span>
      ) : (
        <div className=" flex justify-center h-[90vh] items-center ">
          <i className="fas fa-gear animate-spin text-5xl text-gray-800"></i>
        </div>
      )}

      {/* Global Media Preview Gallery */}
      <Dialog.Root open={!!previewFile} onOpenChange={(open) => !open && setPreviewFile(null)}>
        <Dialog.Portal>
          <Dialog.Overlay 
            className="bg-black/90 backdrop-blur-sm fixed inset-0 z-[1000000] animate-overlayShow" 
            onClick={() => setPreviewFile(null)}
          />
          <Dialog.Content 
            onInteractOutside={(e) => e.preventDefault()}
            className="fixed z-[1000001] inset-0 flex flex-col focus:outline-none animate-contentShow"
          >
            
            {/* Gallery Header */}
            <div className="flex justify-between items-center px-6 py-4 text-white bg-gradient-to-b from-black/50 to-transparent fixed top-0 left-0 right-0 z-50">
               <div className="flex flex-col">
                  <h3 className="font-semibold text-lg drop-shadow-md">
                    {previewFile?.name}
                  </h3>
                  <p className="text-xs text-gray-300 drop-shadow-md">
                    {previewFile && `${bytesToMB(previewFile.size)} • ${ParseDate(previewFile.timeCreated)}`}
                  </p>
               </div>
               <div className="flex items-center gap-2">
                  <button 
                    onClick={() => {
                        const elem = galleryMediaRef.current;
                        if (elem && document.fullscreenEnabled) {
                          if (!document.fullscreenElement) {
                            elem.requestFullscreen().catch(err => console.error(err));
                          } else {
                            document.exitFullscreen();
                          }
                        }
                    }}
                    className="p-2 hover:bg-white/20 rounded-full transition-all text-white/90 hover:text-white"
                    title="Fullscreen"
                  >
                    <FaExpand className="text-xl" />
                  </button>
                  <button 
                    onClick={() => handleDownload(previewFile)}
                    className="p-2 hover:bg-white/20 rounded-full transition-all text-white/90 hover:text-white"
                    title="Download"
                  >
                    <IoDownloadOutline className="text-2xl" />
                  </button>
                  <button 
                    onClick={() => setPreviewFile(null)}
                    className="p-2 hover:bg-white/20 rounded-full transition-all text-white/90 hover:text-white"
                  >
                    <IoClose className="text-2xl" />
                  </button>
               </div>
            </div>

            {/* Main Content Area */}
            <div className="flex-1 flex items-center justify-center relative overflow-hidden h-full w-full p-4 md:p-12" onClick={(e) => e.stopPropagation()}>
               {/* Previous Button */}
               <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    const idx = filteredImgUrl.findIndex(f => f.id === previewFile?.id);
                    if (idx !== -1) {
                      const prevIdx = (idx - 1 + filteredImgUrl.length) % filteredImgUrl.length;
                      setPreviewFile(filteredImgUrl[prevIdx]);
                    }
                  }}
                  className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-black/40 hover:bg-black/60 text-white rounded-full transition-all z-40 hidden md:block backdrop-blur-md"
               >
                  <IoChevronBack className="text-3xl" />
               </button>

               {/* Media Content */}
               <div className="max-w-[100%] max-h-[100%] flex items-center justify-center overflow-hidden">
                 {previewFile?.contentType.includes("image") ? (
                   <img 
                      ref={galleryMediaRef}
                      src={previewFile.url} 
                      alt={previewFile.name} 
                      className="max-h-[85vh] max-w-[95vw] w-auto h-auto object-contain shadow-2xl rounded-lg"
                   />
                 ) : previewFile?.contentType.includes("video") ? (
                   <video 
                      ref={galleryMediaRef}
                      src={previewFile.url} 
                      controls 
                      autoPlay
                      className="max-h-[85vh] max-w-[95vw] shadow-2xl rounded-lg bg-black box-border"
                   ></video>
                 ) : (
                   <div className="w-[80vw] max-w-[500px] aspect-sqaure bg-white rounded-2xl flex flex-col items-center justify-center p-12 shadow-2xl">
                     <PiFileTextDuotone className="text-9xl text-gray-300 mb-6" />
                     <p className="text-xl font-bold text-gray-800 break-all text-center">{previewFile?.name}</p>
                     <p className="text-gray-500 mt-2 uppercase font-mono">{previewFile?.contentType}</p>
                   </div>
                 )}
               </div>

                {/* Next Button */}
               <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    const idx = filteredImgUrl.findIndex(f => f.id === previewFile?.id);
                    if (idx !== -1) {
                      const nextIdx = (idx + 1) % filteredImgUrl.length;
                      setPreviewFile(filteredImgUrl[nextIdx]);
                    }
                  }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-black/40 hover:bg-black/60 text-white rounded-full transition-all z-40 hidden md:block backdrop-blur-md"
               >
                  <IoChevronForward className="text-3xl" />
               </button>
            </div>

            {/* Mobile Footer Navigation Hint */}
            <div className="md:hidden absolute bottom-6 w-full text-center text-white/50 text-sm pointer-events-none">
              Swipe or tap edges to navigate
            </div>
            
            {/* Mobile touch zones (invisible) */}
            <div className="md:hidden absolute inset-y-0 left-0 w-1/4 z-30" onClick={(e) => {
                e.stopPropagation();
                const idx = filteredImgUrl.findIndex(f => f.id === previewFile?.id);
                if (idx !== -1) {
                  const prevIdx = (idx - 1 + filteredImgUrl.length) % filteredImgUrl.length;
                  setPreviewFile(filteredImgUrl[prevIdx]);
                }
            }} />
            <div className="md:hidden absolute inset-y-0 right-0 w-1/4 z-30" onClick={(e) => {
                e.stopPropagation();
                const idx = filteredImgUrl.findIndex(f => f.id === previewFile?.id);
                if (idx !== -1) {
                  const nextIdx = (idx + 1) % filteredImgUrl.length;
                  setPreviewFile(filteredImgUrl[nextIdx]);
                }
            }} />

          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

    </div>
  );
};

export default ImageUploader;
